import {
  Button,
  ButtonVariant,
  Grid,
  IconName,
  KebabMenu,
  KebabMenuOption,
  Menu,
  MenuOption,
  MenuVariant,
  Switch,
  Text,
  TextHighlight,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useGetSoftLaunchOrder } from '../../components/SoftLaunch'
import { canAuthorMultiMarket } from '../../constants/featureFlags'
import { SurveyState } from '../../data/gql-gen/dashboard/graphql'
import { useOptionalWaveId } from '../../hooks/useOptionalWaveId'
import { useProjectId } from '../../hooks/useProjectId'
import { useSurveyId } from '../../hooks/useSurveyId'
import InviteTeamDialog from '../../modules/Project/InviteTeamDialog/InviteTeamDialog.container'
import { useProjectData } from '../../modules/Project/Project.hooks'
import { FullLaunchButtonContainer } from '../../modules/Results/FullLaunchButton'
import useStyles from './styles'

enum KebabMenuOptionId {
  Preview,
  Export,
  Duplicate,
  PauseUnpause,
  End
}

interface Props {
  disableShowQuotas: boolean
  surveyState: SurveyState | undefined
  onClickPause: () => void
  onClickEnd: () => void
  onClickDuplicate: () => void
  onClickChartData: () => void
  onClickRawData: () => void
  onClickDataTables: () => void
  onClickPreview: () => void
  exportOptions: KebabMenuOption[]
  onClickExportToPptx: () => void
  onShowQuotasChange: (showQuotas: boolean) => void
  isMultiMarket?: boolean
  // Per-market results view of a MultiMarketV2 project: hide the caption (the
  // page has its own header), hide project-level actions (duplicate), relabel
  // pause/end to act on the market and send "View audience" to the
  // multi-market audiences route.
  multiMarketV2?: boolean
}

const ResultsOptions: React.FC<Props> = (props: Props) => {
  const {
    disableShowQuotas,
    surveyState,
    onClickPause,
    onClickEnd,
    onClickDuplicate,
    onClickChartData,
    onClickRawData,
    onClickDataTables,
    onClickPreview,
    exportOptions,
    onClickExportToPptx,
    onShowQuotasChange,
    isMultiMarket = false,
    multiMarketV2 = false
  } = props
  const { classes } = useStyles()
  const navigate = useNavigate()
  const surveyId = useSurveyId()
  const projectId = useProjectId()
  const waveId = useOptionalWaveId()
  const { project } = useProjectData()

  const { isSoftLaunchOrder, isSoftLaunchInProgress, isSoftLaunchCompleted } =
    useGetSoftLaunchOrder()

  // Results-only viewer: keep charts, filters and exports, drop the rest.
  const mmViewerOnly = multiMarketV2 && !canAuthorMultiMarket()

  const isPaused = surveyState === SurveyState.Paused

  // Paused counts too: pause/unpause is disabled below once the soft launch
  // completes, so full launch is the only way forward. It resumes the survey.
  const canFullLaunch =
    isSoftLaunchOrder &&
    (isSoftLaunchInProgress || isSoftLaunchCompleted) &&
    (surveyState === SurveyState.Live || isPaused)

  const disablePauseUnpauseProject = isSoftLaunchOrder && isSoftLaunchCompleted

  const onChange: (event: React.ChangeEvent<HTMLInputElement>) => void = (
    event
  ) => {
    onShowQuotasChange(event.target.checked)
  }

  // Pause/end act on the whole (single-market) project, or on just the
  // selected market in the multi-market per-market view.
  const pauseEndEntity = multiMarketV2 ? 'market' : 'project'

  const getMainText: () => string = () => {
    switch (surveyState) {
      case SurveyState.Paused:
        return `Unpause ${pauseEndEntity}`
      default:
        return `Pause ${pauseEndEntity}`
    }
  }

  const getIconName: () => IconName = () => {
    switch (surveyState) {
      case SurveyState.Paused:
        return IconName.PlayCircleOutline
      default:
        return IconName.PauseCircleOutline
    }
  }

  const liveOnlyKebabMenuOptions: KebabMenuOption[] = mmViewerOnly
    ? []
    : [
        {
          id: KebabMenuOptionId.PauseUnpause,
          textItem: getMainText(),
          disabled: disablePauseUnpauseProject,
          onClickItem: () => onClickPause(),
          iconName: getIconName()
        },
        {
          id: KebabMenuOptionId.End,
          textItem: `End ${pauseEndEntity}`,
          onClickItem: () => onClickEnd(),
          iconName: IconName.FlagOutlined
        }
      ]
  const commonKebabMenuOptions: KebabMenuOption[] = [
    {
      id: KebabMenuOptionId.Preview,
      textItem: 'Preview survey',
      onClickItem: () => onClickPreview(),
      iconName: IconName.Preview
    },
    ...exportOptions.map((option) => ({
      ...option,
      id: KebabMenuOptionId.Export
    })),
    // Duplicating a single market of a multi-market project isn't supported —
    // multi-market duplication (whole project) is a separate, project-level
    // feature.
    ...(multiMarketV2
      ? []
      : [
          {
            id: KebabMenuOptionId.Duplicate,
            textItem: 'Duplicate and edit',
            onClickItem: () => onClickDuplicate(),
            iconName: IconName.Duplicate
          }
        ])
  ]

  const getKebabMenuOptions: (
    surveyState: SurveyState | undefined
  ) => KebabMenuOption[] = (surveyState) => {
    // On a multi-market survey the menu should be empty
    if (isMultiMarket) {
      return []
    }

    switch (surveyState) {
      case SurveyState.Scheduled:
        return [...commonKebabMenuOptions]
      case SurveyState.Completed:
      case SurveyState.Halted:
        return [...commonKebabMenuOptions]
      default:
        return [...liveOnlyKebabMenuOptions, ...commonKebabMenuOptions]
    }
  }

  const kebabMenuOptions = getKebabMenuOptions(surveyState)

  const options = [
    {
      text: 'Download chart data',
      onClick: onClickChartData
    },
    {
      text: 'Download raw data file',
      onClick: onClickRawData
    },
    {
      text: 'Request data tables',
      onClick: onClickDataTables,
      disabled: isMultiMarket
    },
    {
      text: 'Export as presentation',
      onClick: onClickExportToPptx
    }
  ]

  const requestDataMenuOptions: MenuOption[] = [
    {
      id: 0,
      options
    }
  ]

  const handleViewAudience = () => {
    // Multi-market projects have no survey-scoped audience route; the market's
    // audience lives on the project-level Audiences step.
    navigate(
      multiMarketV2
        ? `/project/${projectId}${
            waveId ? `/wave/${waveId}` : ''
          }/audiences/${surveyId}`
        : `/project/${projectId}/survey/${surveyId}/audience`
    )
  }

  return (
    <Grid container justifyContent="space-between">
      <Grid item xs="auto">
        <Grid container direction="row" alignItems="flex-end">
          {/* The multi-market results page renders its own "Results" header
              (with the combined/per-market toggle), so skip the caption. */}
          {!multiMarketV2 && (
            <Grid item xs="auto" className={classes.resultsText}>
              <Text
                weight={TextWeight.SemiBold}
                highlight={TextHighlight.Standard}
                size={TextSize.lxl}
                marginRight
              >
                Results
              </Text>
            </Grid>
          )}
          {!isMultiMarket && (
            <>
              <Grid item xs="auto">
                <Text
                  weight={TextWeight.Regular}
                  highlight={TextHighlight.Background}
                  size={TextSize.m}
                >
                  Show quotas
                </Text>
              </Grid>
              <Switch first onChange={onChange} disabled={disableShowQuotas} />
            </>
          )}
        </Grid>
      </Grid>
      <Grid
        item
        xs="auto"
        className={
          !isMultiMarket ? classes.rightContainer3 : classes.rightContainer1
        }
      >
        <Grid container direction="row" alignItems="center">
          {!mmViewerOnly && <InviteTeamDialog accountId={project?.accountId} />}
          {!mmViewerOnly && (
            <Grid item xs="auto">
              <Button
                variant={ButtonVariant.SecondaryNoOutline}
                onClick={handleViewAudience}
              >
                View audience
              </Button>
            </Grid>
          )}
          <Grid item xs="auto" className={classes.requestData}>
            <Menu
              variant={MenuVariant.Body}
              menuOptions={requestDataMenuOptions}
              title="Export data"
            />
          </Grid>
          {canFullLaunch && !mmViewerOnly && (
            <FullLaunchButtonContainer isPaused={isPaused} />
          )}
          <Grid
            item
            xs="auto"
            className={classes.kebabMenu}
            data-testid="result-options-kebab"
          >
            <KebabMenu kebabMenuOptions={kebabMenuOptions} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default ResultsOptions
